import { useState } from "react";
import { Artist, Quest } from "@/mocks/artistData";
import { brands } from "@/mocks/brandData";
import type { BrandIPAsset } from "@/mocks/brandData";

interface IPImageGeneratorProps {
  quest: Quest;
  artist: Artist;
  accentColor: string;
  onComplete: (images: GeneratedIPImage[]) => void;
  onTokenSpend: (amount: number) => void;
  onImageGenerated?: (image: GeneratedIPImage) => void;
}

export interface GeneratedIPImage {
  id: string;
  ipIndex: number;
  ipTag: string;
  previewUrl: string;
  outfit: string;
  outfitType: "preset" | "brand";
  brandAsset?: BrandIPAsset;
  brandId?: string;
  background: string;
  style: string;
  tokenCost: number;
  bonusTokens: number;
}

// ─── 기본 의상 프리셋 ──────────────────────────────────────────────────────────
const outfitPresets = [
  { id: "original",    label: "오리지널",  icon: "ri-star-line",         desc: "IP 기본 의상 그대로" },
  { id: "casual",      label: "캐주얼",    icon: "ri-t-shirt-line",      desc: "편안한 일상 스타일" },
  { id: "formal",      label: "포멀",      icon: "ri-briefcase-line",    desc: "정장 / 비즈니스 룩" },
  { id: "streetwear",  label: "스트릿",    icon: "ri-shirt-line",        desc: "힙한 스트릿 패션" },
  { id: "fantasy",     label: "판타지",    icon: "ri-sword-line",        desc: "판타지 / 마법사 의상" },
  { id: "futuristic",  label: "미래적",    icon: "ri-robot-line",        desc: "사이버펑크 / SF 의상" },
  { id: "traditional", label: "전통",      icon: "ri-ancient-gate-line", desc: "한복 / 전통 의상" },
  { id: "sporty",      label: "스포티",    icon: "ri-run-line",          desc: "스포츠 / 아웃도어 룩" },
];

const backgroundOptions = [
  { id: "studio",     label: "스튜디오",  icon: "ri-camera-line",       desc: "깔끔한 단색 배경" },
  { id: "city",       label: "도시",      icon: "ri-building-line",     desc: "현대 도시 거리" },
  { id: "nature",     label: "자연",      icon: "ri-leaf-line",         desc: "숲 / 공원 / 자연" },
  { id: "beach",      label: "해변",      icon: "ri-water-flash-line",  desc: "바다 / 해변" },
  { id: "space",      label: "우주",      icon: "ri-planet-line",       desc: "우주 / 별빛 배경" },
  { id: "neon-city",  label: "네온 시티", icon: "ri-flashlight-fill",   desc: "사이버펑크 도시" },
  { id: "fantasy-bg", label: "판타지",    icon: "ri-magic-line",        desc: "마법 / 환상 세계" },
  { id: "minimal",    label: "미니멀",    icon: "ri-subtract-line",     desc: "흰색 / 그라데이션" },
];

const styleOptions = [
  { id: "realistic",    label: "실사",       icon: "ri-image-line",         color: "#00f5d4" },
  { id: "anime",        label: "애니메이션", icon: "ri-emotion-happy-line", color: "#f72585" },
  { id: "3d",           label: "3D 렌더",    icon: "ri-box-3-line",         color: "#ffd60a" },
  { id: "illustration", label: "일러스트",   icon: "ri-brush-line",         color: "#c77dff" },
  { id: "cinematic",    label: "시네마틱",   icon: "ri-film-line",          color: "#ff6b35" },
];

const aiImageTools = [
  { id: "nanobanana", name: "Nanobanana",        icon: "ri-seedling-line",   badge: "추천",   color: "#39ff14", desc: "IP 캐릭터 특화 이미지 생성" },
  { id: "midjourney", name: "Midjourney",        icon: "ri-palette-line",    badge: "고품질", color: "#00f5d4", desc: "최고 품질의 AI 이미지 생성" },
  { id: "dalle",      name: "DALL-E 3",          icon: "ri-openai-line",     badge: "빠름",   color: "#ffd60a", desc: "OpenAI의 이미지 생성 모델" },
  { id: "stable",     name: "Stable Diffusion",  icon: "ri-contrast-2-line", badge: "무료",   color: "#c77dff", desc: "오픈소스 이미지 생성 모델" },
];

const TOKEN_COST_PER_IMAGE = 50;

const previewImages: Record<string, string> = {
  "0-original":   "https://public.readdy.ai/ai/img_res/7a78628a5b9a346847d05517990310b4.jpg",
  "0-casual":     "https://readdy.ai/api/search-image?query=anime%20character%20casual%20streetwear%20outfit%2C%20hoodie%20jeans%20sneakers%2C%20urban%20background%2C%20colorful%20digital%20illustration%2C%20full%20body%20character%20art&width=400&height=400&seq=ip-gen-0-cas&orientation=squarish",
  "0-streetwear": "https://readdy.ai/api/search-image?query=anime%20character%20hip%20hop%20streetwear%20fashion%2C%20oversized%20jacket%20cap%2C%20graffiti%20wall%20background%2C%20vibrant%20neon%20colors%2C%20full%20body%20digital%20art&width=400&height=400&seq=ip-gen-0-str&orientation=squarish",
  "0-futuristic": "https://readdy.ai/api/search-image?query=anime%20character%20cyberpunk%20futuristic%20outfit%2C%20neon%20lights%20city%20background%2C%20sci-fi%20armor%20suit%2C%20glowing%20elements%2C%20full%20body%20digital%20art&width=400&height=400&seq=ip-gen-0-fut&orientation=squarish",
  "0-fantasy":    "https://readdy.ai/api/search-image?query=anime%20character%20fantasy%20mage%20outfit%2C%20magical%20forest%20background%2C%20glowing%20staff%2C%20mystical%20atmosphere%2C%20full%20body%20digital%20illustration&width=400&height=400&seq=ip-gen-0-fan&orientation=squarish",
  "1-original":   "https://readdy.ai/api/search-image?query=cute%20anime%20character%20original%20design%2C%20pastel%20colors%2C%20soft%20background%2C%20kawaii%20style%2C%20full%20body%20character%20illustration%2C%20high%20quality&width=400&height=400&seq=ip-gen-1-orig&orientation=squarish",
  "1-casual":     "https://readdy.ai/api/search-image?query=cute%20anime%20character%20casual%20outfit%2C%20summer%20dress%2C%20park%20background%2C%20soft%20pastel%20colors%2C%20full%20body%20kawaii%20illustration&width=400&height=400&seq=ip-gen-1-cas&orientation=squarish",
  "1-formal":     "https://readdy.ai/api/search-image?query=cute%20anime%20character%20formal%20elegant%20dress%2C%20ballroom%20background%2C%20sophisticated%20style%2C%20full%20body%20digital%20art%20illustration&width=400&height=400&seq=ip-gen-1-form&orientation=squarish",
  "brand":        "https://readdy.ai/api/search-image?query=anime%20character%20wearing%20branded%20fashion%20streetwear%2C%20product%20placement%2C%20stylish%20outfit%20with%20logo%2C%20urban%20background%2C%20high%20quality%20digital%20art%2C%20full%20body%20illustration&width=400&height=400&seq=ip-gen-brand&orientation=squarish",
  "default":      "https://readdy.ai/api/search-image?query=anime%20character%20stylish%20outfit%2C%20dynamic%20pose%2C%20colorful%20background%2C%20high%20quality%20digital%20art%2C%20full%20body%20illustration%2C%20vibrant%20colors&width=400&height=400&seq=ip-gen-default&orientation=squarish",
};

function getPreviewImage(ipIndex: number, outfitId: string, isBrand: boolean): string {
  if (isBrand) return previewImages["brand"];
  return previewImages[`${ipIndex}-${outfitId}`] ?? previewImages["default"];
}

// ─── 의상 탭 타입 ──────────────────────────────────────────────────────────────
type OutfitTab = "preset" | "brand";

export default function IPImageGenerator({
  quest, artist, accentColor, onComplete, onTokenSpend, onImageGenerated,
}: IPImageGeneratorProps) {
  const [selectedIP, setSelectedIP]           = useState<number>(0);
  const [outfitTab, setOutfitTab]             = useState<OutfitTab>("preset");
  const [selectedOutfit, setSelectedOutfit]   = useState("original");
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [selectedBrandAsset, setSelectedBrandAsset] = useState<BrandIPAsset | null>(null);
  const [selectedBg, setSelectedBg]           = useState("studio");
  const [selectedStyle, setSelectedStyle]     = useState("realistic");
  const [selectedTool, setSelectedTool]       = useState("nanobanana");
  const [customDesc, setCustomDesc]           = useState("");
  const [isGenerating, setIsGenerating]       = useState(false);
  const [generatedImages, setGeneratedImages] = useState<GeneratedIPImage[]>([]);

  // 패션 카테고리 브랜드만 필터
  const fashionBrands = brands.filter(b => b.category === "fashion" || b.ipAssets.some(a => a.type === "fashion"));

  const totalTokens   = generatedImages.reduce((s, g) => s + g.tokenCost, 0);
  const totalBonus    = generatedImages.reduce((s, g) => s + g.bonusTokens, 0);

  const currentOutfitLabel = (() => {
    if (outfitTab === "brand" && selectedBrandAsset) return selectedBrandAsset.name;
    return outfitPresets.find(o => o.id === selectedOutfit)?.label ?? selectedOutfit;
  })();

  const handleGenerate = () => {
    if (isGenerating) return;
    if (outfitTab === "brand" && !selectedBrandAsset) return;
    setIsGenerating(true);

    setTimeout(() => {
      const bg    = backgroundOptions.find(b => b.id === selectedBg)?.label ?? selectedBg;
      const style = styleOptions.find(s => s.id === selectedStyle)?.label ?? selectedStyle;
      const isBrand = outfitTab === "brand";
      const bonusTokens = isBrand && selectedBrandAsset ? selectedBrandAsset.tokenBonus : 0;

      const newImg: GeneratedIPImage = {
        id: `gen-${Date.now()}`,
        ipIndex: selectedIP,
        ipTag: `@IP${selectedIP + 1}`,
        previewUrl: getPreviewImage(selectedIP, selectedOutfit, isBrand),
        outfit: currentOutfitLabel,
        outfitType: isBrand ? "brand" : "preset",
        brandAsset: isBrand && selectedBrandAsset ? selectedBrandAsset : undefined,
        brandId: isBrand ? (selectedBrandId ?? undefined) : undefined,
        background: bg,
        style,
        tokenCost: TOKEN_COST_PER_IMAGE,
        bonusTokens,
      };

      setGeneratedImages(prev => {
        const filtered = prev.filter(g => g.ipIndex !== selectedIP);
        return [...filtered, newImg];
      });
      onTokenSpend(TOKEN_COST_PER_IMAGE);
      if (onImageGenerated) onImageGenerated(newImg);
      setIsGenerating(false);
    }, 2500);
  };

  const handleRemove = (id: string) => {
    setGeneratedImages(prev => prev.filter(g => g.id !== id));
  };

  const canGenerate = outfitTab === "preset" || (outfitTab === "brand" && !!selectedBrandAsset);
  const canProceed  = generatedImages.length > 0;

  return (
    <div className="space-y-5">

      {/* 헤더 */}
      <div className="rounded-xl p-4 border" style={{ borderColor: `${accentColor}25`, background: `${accentColor}06` }}>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: `${accentColor}20` }}>
            <i className="ri-image-ai-line text-base" style={{ color: accentColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white/80 text-sm font-semibold mb-1">IP 이미지 커스터마이징</p>
            <p className="text-white/40 text-xs leading-relaxed">
              IP 캐릭터의 의상·배경·스타일을 변경해 나만의 이미지를 생성하세요.
              <strong className="text-white/60"> 브랜드 의상</strong> 선택 시 토큰 보너스가 추가 지급됩니다.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <div className="flex items-center gap-1">
                <i className="ri-coin-fill text-xs" style={{ color: accentColor }} />
                <span className="text-xs font-bold" style={{ color: accentColor }}>1장당 {TOKEN_COST_PER_IMAGE} 토큰 소모</span>
              </div>
              {totalTokens > 0 && (
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: `${accentColor}15`, border: `1px solid ${accentColor}30` }}>
                  <span className="text-xs font-bold" style={{ color: accentColor }}>사용 {totalTokens}</span>
                  {totalBonus > 0 && (
                    <span className="text-xs font-bold text-[#f5e642]"> / 보너스 +{totalBonus}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* IP 선택 */}
      <div>
        <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2.5">어떤 IP를 변경할까요?</p>
        <div className="flex flex-wrap gap-2">
          {artist.ipImages.map((img, i) => {
            const isSelected  = selectedIP === i;
            const isGenerated = generatedImages.some(g => g.ipIndex === i);
            return (
              <button
                key={i}
                onClick={() => setSelectedIP(i)}
                className="relative flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all cursor-pointer"
                style={isSelected
                  ? { borderColor: `${accentColor}60`, background: `${accentColor}15` }
                  : { borderColor: "rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.03)" }
                }
              >
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-white/10">
                  <img src={img} alt={`IP ${i + 1}`} className="w-full h-full object-cover object-top" />
                </div>
                <div className="text-left">
                  <p className="text-xs font-bold" style={{ color: isSelected ? accentColor : "rgba(255,255,255,0.6)" }}>@IP{i + 1}</p>
                  <p className="text-[10px] text-white/30">{isGenerated ? "생성 완료" : "미생성"}</p>
                </div>
                {isGenerated && (
                  <div className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full" style={{ background: accentColor }}>
                    <i className="ri-check-line text-[10px] text-black font-bold" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* ── 왼쪽: 커스터마이징 옵션 ── */}
        <div className="space-y-4">

          {/* 의상 탭 */}
          <div>
            <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">의상 선택</p>

            {/* 탭 스위처 */}
            <div className="flex gap-1 p-1 rounded-xl bg-white/5 border border-white/8 mb-3">
              <button
                onClick={() => setOutfitTab("preset")}
                className="flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
                style={outfitTab === "preset"
                  ? { background: `${accentColor}20`, color: accentColor }
                  : { color: "rgba(255,255,255,0.35)" }
                }
              >
                <i className="ri-t-shirt-line mr-1" />
                기본 의상
              </button>
              <button
                onClick={() => setOutfitTab("brand")}
                className="flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
                style={outfitTab === "brand"
                  ? { background: "rgba(245,230,66,0.15)", color: "#f5e642" }
                  : { color: "rgba(255,255,255,0.35)" }
                }
              >
                <i className="ri-building-2-line" />
                브랜드 의상
                <span className="text-[9px] font-bold px-1 py-px rounded" style={{ background: "rgba(245,230,66,0.2)", color: "#f5e642" }}>
                  보너스
                </span>
              </button>
            </div>

            {/* 기본 의상 프리셋 */}
            {outfitTab === "preset" && (
              <div className="grid grid-cols-2 gap-1.5">
                {outfitPresets.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setSelectedOutfit(opt.id)}
                    title={opt.desc}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all cursor-pointer text-left"
                    style={selectedOutfit === opt.id
                      ? { borderColor: `${accentColor}60`, background: `${accentColor}15`, color: accentColor }
                      : { borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", color: "rgba(255,255,255,0.45)" }
                    }
                  >
                    <i className={`${opt.icon} text-sm flex-shrink-0`} />
                    <span className="truncate">{opt.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* 브랜드 의상 */}
            {outfitTab === "brand" && (
              <div className="space-y-3">
                {/* 브랜드 선택 */}
                <div className="flex flex-wrap gap-1.5">
                  {brands.map(b => (
                    <button
                      key={b.id}
                      onClick={() => { setSelectedBrandId(b.id); setSelectedBrandAsset(null); }}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all cursor-pointer"
                      style={selectedBrandId === b.id
                        ? { borderColor: `${b.accentColor}60`, background: `${b.accentColor}15`, color: b.accentColor }
                        : { borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", color: "rgba(255,255,255,0.45)" }
                      }
                    >
                      <div className="w-4 h-4 rounded overflow-hidden flex-shrink-0">
                        <img src={b.logo} alt={b.name} className="w-full h-full object-cover" />
                      </div>
                      {b.name}
                    </button>
                  ))}
                </div>

                {/* 선택된 브랜드의 IP 에셋 */}
                {selectedBrandId && (() => {
                  const brand = brands.find(b => b.id === selectedBrandId);
                  if (!brand) return null;
                  return (
                    <div className="space-y-1.5">
                      {brand.ipAssets.map(asset => {
                        const isSelected = selectedBrandAsset?.id === asset.id;
                        return (
                          <button
                            key={asset.id}
                            onClick={() => setSelectedBrandAsset(isSelected ? null : asset)}
                            className="w-full flex items-center gap-3 p-2.5 rounded-xl border text-left transition-all cursor-pointer"
                            style={isSelected
                              ? { borderColor: `${brand.accentColor}50`, background: `${brand.accentColor}10` }
                              : { borderColor: "rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)" }
                            }
                          >
                            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-white/10">
                              <img src={asset.image} alt={asset.name} className="w-full h-full object-cover object-top" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <span className="text-white/80 text-xs font-semibold truncate">{asset.name}</span>
                                <span className="font-mono text-[10px] px-1.5 py-px rounded flex-shrink-0"
                                  style={{ background: `${brand.accentColor}20`, color: brand.accentColor }}>
                                  {asset.tag}
                                </span>
                              </div>
                              <p className="text-white/35 text-[11px] truncate">{asset.description}</p>
                            </div>
                            <div className="flex items-center gap-1 px-2 py-1 rounded-lg flex-shrink-0"
                              style={{ background: "rgba(245,230,66,0.15)" }}>
                              <i className="ri-coin-fill text-xs text-[#f5e642]" />
                              <span className="text-xs font-bold text-[#f5e642]">+{asset.tokenBonus}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}

                {!selectedBrandId && (
                  <p className="text-white/25 text-xs text-center py-3">브랜드를 선택하면 의상 에셋이 표시됩니다</p>
                )}
              </div>
            )}
          </div>

          {/* 배경 */}
          <div>
            <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">배경</p>
            <div className="grid grid-cols-2 gap-1.5">
              {backgroundOptions.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setSelectedBg(opt.id)}
                  title={opt.desc}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all cursor-pointer text-left"
                  style={selectedBg === opt.id
                    ? { borderColor: `${accentColor}60`, background: `${accentColor}15`, color: accentColor }
                    : { borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", color: "rgba(255,255,255,0.45)" }
                  }
                >
                  <i className={`${opt.icon} text-sm flex-shrink-0`} />
                  <span className="truncate">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 스타일 */}
          <div>
            <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">이미지 스타일</p>
            <div className="flex flex-wrap gap-1.5">
              {styleOptions.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setSelectedStyle(opt.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                  style={selectedStyle === opt.id
                    ? { borderColor: `${opt.color}60`, background: `${opt.color}15`, color: opt.color }
                    : { borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", color: "rgba(255,255,255,0.45)" }
                  }
                >
                  <i className={`${opt.icon} text-sm`} style={selectedStyle === opt.id ? { color: opt.color } : {}} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 추가 설명 */}
          <div>
            <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
              추가 설명 <span className="text-white/25 font-normal normal-case">(선택)</span>
            </p>
            <textarea
              value={customDesc}
              onChange={e => setCustomDesc(e.target.value)}
              placeholder="예: 빨간 머리카락, 안경 착용, 미소 짓는 표정..."
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white placeholder-white/20 outline-none focus:border-white/25 transition-colors resize-none"
              rows={2}
              maxLength={150}
            />
          </div>

          {/* AI 도구 */}
          <div>
            <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">이미지 생성 도구</p>
            <div className="grid grid-cols-2 gap-1.5">
              {aiImageTools.map(tool => (
                <button
                  key={tool.id}
                  onClick={() => setSelectedTool(tool.id)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all cursor-pointer text-left"
                  style={selectedTool === tool.id
                    ? { borderColor: `${tool.color}60`, background: `${tool.color}12` }
                    : { borderColor: "rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)" }
                  }
                >
                  <div className="w-6 h-6 flex items-center justify-center rounded-md flex-shrink-0" style={{ background: `${tool.color}20` }}>
                    <i className={`${tool.icon} text-xs`} style={{ color: tool.color }} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="font-semibold truncate text-xs" style={{ color: selectedTool === tool.id ? tool.color : "rgba(255,255,255,0.6)" }}>
                        {tool.name}
                      </span>
                      <span className="text-[9px] font-bold px-1 py-px rounded flex-shrink-0" style={{ background: `${tool.color}20`, color: tool.color }}>
                        {tool.badge}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── 오른쪽: 미리보기 + 생성 ── */}
        <div className="space-y-4">
          <div>
            <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">미리보기</p>
            <div className="rounded-xl overflow-hidden border border-white/10 relative" style={{ aspectRatio: "1/1" }}>
              {isGenerating ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-3" style={{ background: `${accentColor}08` }}>
                  <div className="relative w-16 h-16">
                    <div className="absolute inset-0 rounded-full border-2 border-white/10" />
                    <div className="absolute inset-0 rounded-full border-2 border-t-transparent animate-spin"
                      style={{ borderColor: `${accentColor}60`, borderTopColor: "transparent" }} />
                    <div className="absolute inset-2 rounded-full flex items-center justify-center" style={{ background: `${accentColor}15` }}>
                      <i className="ri-image-ai-line text-xl" style={{ color: accentColor }} />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-white/60 text-xs font-semibold">@IP{selectedIP + 1} 이미지 생성 중...</p>
                    <p className="text-white/30 text-[11px] mt-1">{currentOutfitLabel} · {backgroundOptions.find(b => b.id === selectedBg)?.label}</p>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: `${accentColor}15`, border: `1px solid ${accentColor}30` }}>
                    <i className="ri-coin-fill text-xs" style={{ color: accentColor }} />
                    <span className="text-xs font-bold" style={{ color: accentColor }}>{TOKEN_COST_PER_IMAGE} 토큰 소모 중</span>
                  </div>
                </div>
              ) : (() => {
                const existing = generatedImages.find(g => g.ipIndex === selectedIP);
                if (existing) {
                  const brand = existing.brandId ? brands.find(b => b.id === existing.brandId) : null;
                  return (
                    <div className="relative w-full h-full">
                      <img src={existing.previewUrl} alt="Generated IP" className="w-full h-full object-cover object-top" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute bottom-3 left-3 right-3 space-y-1.5">
                        <div className="flex flex-wrap gap-1">
                          {[existing.outfit, existing.background, existing.style].map((tag, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-black/50 text-white/70 border border-white/15">
                              {tag}
                            </span>
                          ))}
                        </div>
                        {brand && existing.brandAsset && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg w-fit"
                            style={{ background: `${brand.accentColor}25`, border: `1px solid ${brand.accentColor}40` }}>
                            <div className="w-3 h-3 rounded overflow-hidden flex-shrink-0">
                              <img src={brand.logo} alt={brand.name} className="w-full h-full object-cover" />
                            </div>
                            <span className="text-[10px] font-bold" style={{ color: brand.accentColor }}>{existing.brandAsset.tag}</span>
                            <span className="text-[10px] text-[#f5e642] font-bold">+{existing.bonusTokens}</span>
                          </div>
                        )}
                      </div>
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-lg"
                        style={{ background: `${accentColor}25`, border: `1px solid ${accentColor}50` }}>
                        <i className="ri-check-double-line text-xs" style={{ color: accentColor }} />
                        <span className="text-[10px] font-bold" style={{ color: accentColor }}>생성 완료</span>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-white/3">
                    <div className="w-16 h-16 rounded-xl overflow-hidden border border-white/10">
                      <img src={artist.ipImages[selectedIP]} alt={`IP ${selectedIP + 1}`} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="text-center px-4">
                      <p className="text-white/40 text-xs">@IP{selectedIP + 1} 원본</p>
                      <p className="text-white/25 text-[11px] mt-1">옵션을 선택하고 생성 버튼을 누르세요</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* 브랜드 의상 선택 안내 */}
          {outfitTab === "brand" && !selectedBrandAsset && (
            <div className="rounded-xl p-3 border border-[#f5e642]/20 bg-[#f5e642]/5 flex items-center gap-2">
              <i className="ri-information-line text-sm text-[#f5e642]" />
              <p className="text-[11px] text-[#f5e642]/70">왼쪽에서 브랜드 에셋을 선택하면 생성할 수 있어요</p>
            </div>
          )}

          {/* 생성 버튼 */}
          <button
            onClick={handleGenerate}
            disabled={isGenerating || !canGenerate}
            className="w-full py-3.5 rounded-xl font-bold text-sm text-white transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: `linear-gradient(135deg, ${accentColor}, ${artist.secondaryColor})` }}
          >
            {isGenerating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                생성 중...
              </>
            ) : (
              <>
                <i className="ri-image-ai-line text-base" />
                @IP{selectedIP + 1} 이미지 생성
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/20 text-xs">
                  <i className="ri-coin-fill text-xs" />
                  {TOKEN_COST_PER_IMAGE}
                  {outfitTab === "brand" && selectedBrandAsset && (
                    <span className="text-[#f5e642]"> +{selectedBrandAsset.tokenBonus} 보너스</span>
                  )}
                </span>
              </>
            )}
          </button>

        </div>
      </div>

      {/* 생성 완료 안내 */}
      {canProceed && (
        <div className="rounded-xl p-3 border flex items-center gap-2.5"
          style={{ borderColor: `${accentColor}30`, background: `${accentColor}08` }}>
          <div className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0"
            style={{ background: `${accentColor}25` }}>
            <i className="ri-check-line text-xs" style={{ color: accentColor }} />
          </div>
          <p className="text-xs" style={{ color: `${accentColor}cc` }}>
            <strong>{generatedImages.length}개</strong> IP 이미지가 프롬프트 위 리스트에 추가됐어요. 클릭해서 프롬프트에 삽입하세요!
          </p>
        </div>
      )}
    </div>
  );
}
